import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock, CreditCard, MapPin, ShieldCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/Reveal";
import { fmt } from "../lib/api";
import { NEIGHBORHOODS, SITE } from "../config/site";

export default function NeighborhoodPage() {
  const { slug } = useParams();
  const area = NEIGHBORHOODS.find((n) => n.slug === slug);

  if (!area) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="area-not-found">
        <h1 className="font-display text-3xl font-black text-brand-forest">Area not found</h1>
        <Link to="/delivery" data-testid="area-not-found-link" className="mt-6 inline-block rounded-full bg-brand-pine px-8 py-4 text-sm font-bold text-brand-cream">
          See All Delivery Areas
        </Link>
      </div>
    );
  }

  const nearby = NEIGHBORHOODS.filter((n) => n.region === area.region && n.slug !== slug).slice(0, 6);

  return (
    <div data-testid="neighborhood-page">
      <SEO
        title={`Cannabis Delivery in ${area.name} (${area.zip})`}
        path={`/areas/${area.slug}`}
        description={`CannaCheap DC delivers premium cannabis to ${area.name} ${area.zip}. ${area.blurb} 30–90 minute delivery, free on orders $50+. 21+.`}
        jsonLd={{
          "@context": "https://schema.org", "@type": "LocalBusiness",
          name: SITE.name, telephone: "+1-202-422-9366",
          areaServed: { "@type": "Place", name: `${area.name}, ${area.region} ${area.zip}` },
          openingHours: "Mo-Su 10:00-22:00",
        }}
      />
      <div className="bg-brand-forest py-16 text-brand-cream lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <p className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-gold">
            <MapPin className="h-4 w-4" /> {area.region} · {area.zip}
          </p>
          <MaskedLines lines={[`${area.name}`, "Delivery."]} className="mt-4 text-4xl font-black leading-[1.0] sm:text-5xl lg:text-6xl" />
          <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-cream/70">{area.blurb}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/shop" data-testid="area-shop-cta" className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest transition-transform hover:scale-[1.04]">
              Order to {area.name} <ArrowRight className="h-4 w-4" />
            </Link>
            <a href={SITE.phoneHref} data-testid="area-call-cta" className="rounded-full border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:border-brand-gold">
              {SITE.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10">
        <div className="grid gap-6 sm:grid-cols-3">
          {[
            { icon: Clock, title: "30–90 minutes", text: `Typical delivery window to ${area.name} depending on volume.` },
            { icon: CreditCard, title: "Flexible payment", text: `${SITE.payments.join(", ")} accepted. Free delivery on orders ${fmt(SITE.freeDeliveryMinimum)}+.` },
            { icon: ShieldCheck, title: "21+ verified", text: "Have your valid government ID ready at the door." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-brand-line bg-white p-8">
                <c.icon className="h-6 w-6 text-brand-pine" />
                <h2 className="mt-4 font-display text-lg font-bold text-brand-forest">{c.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {nearby.length > 0 && (
          <div className="mt-16">
            <Reveal>
              <h2 className="text-2xl font-black text-brand-forest">Also delivering nearby</h2>
            </Reveal>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {nearby.map((n) => (
                <Link key={n.slug} to={`/areas/${n.slug}`} data-testid={`nearby-${n.slug}`}
                  className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium text-brand-ink/75 transition-all hover:border-brand-gold hover:text-brand-forest">
                  {n.name}
                </Link>
              ))}
              <Link to="/delivery" data-testid="nearby-all" className="rounded-full bg-brand-pine px-5 py-2.5 text-sm font-bold text-brand-cream">
                All areas
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
